// Home del Portal Vendedor — réplica del mock `portal-vendedor.html`.
//
// Layout del mock, 1:1:
//   · KPI bar densa con lo que el silo SÍ calcula: ventas hoy, meta con barrita, ranking de tienda
//     y prospectos. La comisión, la promo y el turno no tienen fuente y por eso no se pintan.
//   · Grid 2fr / 1fr:
//       izquierda  → "Mis ventas de hoy" + "Catálogo · top de mi tienda hoy"
//       derecha    → "Meta del día" + "Prospectos pendientes" + "Alertas" + "Acciones rápidas"
//
// Todo sale del silo: KPIs y alertas de `GET /seller/home`, ventas de `GET /sales`.
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { BookOpen, Clock, Plus, Search } from 'lucide-react';
import { Badge, Loading } from '@/kit';
import { KpiBar, type KpiItem } from '@/shared/patterns/KpiBar';
import { SaleTicketProduct, ticketTitle } from '@/shared/patterns/SaleTicket';
import { formatMXN, formatShortMXN } from '@/shared/lib/format/money';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import { useCatalog, useProspects } from '@/domains/sales/wizard/api/useWizard';
import { useSellerContext } from '@/domains/sales/wizard/sellerContext';
import {
    isProspectOpen,
    prospectBucket,
    prospectName,
    prospectPhone,
    todayMx,
} from '@/domains/sales/wizard/prospects';
import type { Prospect } from '@/domains/sales/wizard/types';
import { useRecentSales, useSellerAlerts, useSellerKpis } from './api/usePortal';
import { PaymentBadge } from './components/PaymentBadge';
import { HomeCard, HomeEmpty, HomeRow } from './components/HomeCard';
import { SaleStatusBadge } from './components/SaleStatusBadge';
import { AlertsCard } from './components/AlertsCard';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { saleAmountCents } from './mysales/lib/mySalesFilters';
import { variantCapacityLabel } from '@/shared/lib/format/capacity';

/** Sin precio registrado se pinta «—», nunca $0. */
function priceLabel(cents: number | null | undefined): string {
    return cents == null ? '—' : formatMXN(cents / 100);
}

export function SalesHome() {
    const navigate = useNavigate();
    const { sellerId } = useSellerContext();
    const kpisQuery = useSellerKpis();
    const kpis = kpisQuery.data;
    const sales = useRecentSales();
    const prospectsQuery = useProspects(sellerId);
    const prospects = prospectsQuery.data;
    const alerts = useSellerAlerts();
    const catalog = useCatalog('portal');

    // El día de hoy se calcula fuera del render (regla de pureza), igual que en SalesProspects.
    const [todayDay, setTodayDay] = useState('');
    useEffect(() => {
        const id = setTimeout(() => {
            setTodayDay(todayMx());
        }, 0);
        return () => {
            clearTimeout(id);
        };
    }, []);

    // Un `/seller/home` caído dejaba el Home en spinner eterno: la pantalla nunca decía qué pasó.
    if (kpisQuery.isError) {
        return (
            <div role="alert" className="px-3 py-2.5 text-[11.5px]">
                <p>No se pudo leer el tablero de tu tienda.</p>
                <button
                    type="button"
                    className="mt-1 text-primary hover:underline"
                    onClick={() => void kpisQuery.refetch()}
                >
                    Reintentar
                </button>
            </div>
        );
    }
    if (!kpis) return <Loading size="lg" />;

    const goSection = (slug: string) => () =>
        void navigate({ to: '/sales/$section', params: { section: slug } });
    // Prospectos es la pestaña de Clientes.
    const goProspects = () =>
        void navigate({
            to: '/sales/$section',
            params: { section: 'customers' },
            search: { tab: 'prospects' },
        });
    const goWizard = () => void navigate({ to: '/wizard' });

    // La cancelada se queda en la lista con su badge: esconderla le borra al vendedor una venta
    // que sí hizo. Lo que no hace es contar para nada derivado.
    const soldToday = sales.rows.filter((s) => s.today);
    const soldPesos = kpis.salesTodayCents / 100;
    const goalPesos = kpis.dailyGoalCents / 100;
    // Sin meta configurada NO hay porcentaje: dividir entre cero daba `NaN%`, una barra 100% verde
    // (el ancho inválido lo hereda del padre) y una felicitación con excedente negativo.
    const hasGoal = kpis.dailyGoalCents > 0;
    const goalPct = hasGoal ? Math.round((soldPesos / goalPesos) * 100) : 0;
    const missing = goalPesos - soldPesos;
    // El 0 del silo significa «todavía sin posición» (no vendió hoy), no «último lugar».
    const hasRanking = kpis.rankingPosition > 0;

    // Top del catálogo: primero lo vendido hoy en la tienda, luego el resto por prioridad (mock
    // `renderCatalogo`). Máximo 8 filas.
    const soldCount = new Map<string, number>();
    for (const s of soldToday) {
        if (s.status === 'CANCELLED' || s.sku === null) continue;
        soldCount.set(s.sku, (soldCount.get(s.sku) ?? 0) + 1);
    }
    const topCatalog = [...(catalog.data?.items ?? [])]
        .sort(
            (a, b) =>
                (soldCount.get(b.sku) ?? 0) - (soldCount.get(a.sku) ?? 0) ||
                b.priorityRank - a.priorityRank,
        )
        .slice(0, 8);

    // Sin lista leída, los conteos de la pantalla valen cero por ausencia de datos, no porque el
    // vendedor no tenga prospectos: ahí el KPI se queda con lo que sí respondió el silo.
    const hasProspectList = prospects !== undefined;
    const open = (prospects?.items ?? []).filter(isProspectOpen);
    const overdue = open.filter((p) => prospectBucket(p, todayDay) === 'overdue');
    const today = open.filter((p) => prospectBucket(p, todayDay) === 'today');
    const upcoming = open.filter((p) => prospectBucket(p, todayDay) === 'upcoming').slice(0, 3);

    // Al paso 01 con el teléfono cargado y el prospecto vinculado: no guarda la sesión del wizard.
    const resume = (p: Prospect) => () => {
        const phone = prospectPhone(p);
        void navigate({
            to: '/wizard',
            search: phone ? { phone, prospect: p.prospectId } : { prospect: p.prospectId },
        });
    };

    const items: KpiItem[] = [
        { label: 'Ventas hoy', value: kpis.salesTodayCount, sub: formatShortMXN(soldPesos) },
        {
            label: 'Meta día',
            value: hasGoal ? `${String(goalPct)}%` : 'Sin meta',
            tone: hasGoal && goalPct >= 100 ? 'success' : 'default',
            sub: hasGoal ? (
                <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-[5px] w-[60px] overflow-hidden rounded-full bg-muted align-middle">
                        <span
                            className="block h-full rounded-full bg-success"
                            style={{ width: `${String(Math.min(goalPct, 100))}%` }}
                        />
                    </span>
                    de {formatShortMXN(goalPesos)}
                </span>
            ) : (
                'Sin meta configurada'
            ),
        },
        {
            label: 'Ranking tienda',
            value: hasRanking
                ? `#${String(kpis.rankingPosition)}/${String(kpis.rankingTotal)}`
                : '—',
            tone: 'info',
            sub: !hasRanking
                ? 'Todavía sin posición'
                : kpis.rankingPosition === 1
                  ? 'Vas primero'
                  : `de ${String(kpis.rankingTotal)} vendedores`,
        },
        {
            label: 'Prospectos hoy',
            value: hasProspectList ? today.length : kpis.prospectsTodayCount,
            sub: `vencidos ${String(hasProspectList ? overdue.length : kpis.prospectsOverdueCount)}`,
            tone: overdue.length > 0 ? 'warning' : 'default',
        },
    ];

    return (
        <div className="flex flex-col gap-2.5">
            <KpiBar items={items} className="rounded-md" />

            <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-3">
                {/* Columna izquierda — ventas + catálogo */}
                <div className="flex min-h-0 flex-col gap-2.5 lg:col-span-2">
                    <HomeCard
                        title="Mis ventas de hoy"
                        meta={
                            <button
                                type="button"
                                className="text-primary hover:underline"
                                onClick={goSection('my-sales')}
                            >
                                Ver todas →
                            </button>
                        }
                        bodyClassName="max-h-[280px]"
                    >
                        {sales.error ? (
                            // Una lectura fallida NO se pinta como «sin ventas hoy»: el vendedor
                            // concluiría que no vendió nada.
                            <div role="alert" className="px-3 py-2.5 text-[11px]">
                                <p>No se pudieron leer tus ventas.</p>
                                <button
                                    type="button"
                                    className="mt-1 text-primary hover:underline"
                                    onClick={sales.refetch}
                                >
                                    Reintentar
                                </button>
                            </div>
                        ) : soldToday.length === 0 ? (
                            <HomeEmpty>Sin ventas cerradas hoy. ¡Arranca con la primera!</HomeEmpty>
                        ) : (
                            soldToday.map((s) => (
                                <HomeRow key={s.saleRef} onClick={goSection('my-sales')}>
                                    <span className="w-10 flex-none font-mono text-[10.5px] text-muted-foreground">
                                        {s.atTime}
                                    </span>
                                    <span className="min-w-[130px] flex-1 truncate font-medium">
                                        {saleCustomerLabel(s)}
                                    </span>
                                    <span
                                        className="min-w-0 flex-1 text-[10.5px] text-muted-foreground"
                                        title={ticketTitle(s, s.product)}
                                    >
                                        <SaleTicketProduct
                                            sale={s}
                                            category={null}
                                            saleName={s.product}
                                        />
                                    </span>
                                    <span className="flex-none">
                                        <PaymentBadge
                                            method={s.method}
                                            payments={s.payments}
                                            amountCents={s.amountCents}
                                        />
                                    </span>
                                    {/* El estado solo se pinta cuando NO es una venta buena: un
                                        badge en cada fila esconde justo la que importa. */}
                                    {s.status === 'CANCELLED' ? (
                                        <span className="flex-none">
                                            <SaleStatusBadge status={s.status} />
                                        </span>
                                    ) : null}
                                    {/* Lo vendido, igual que Mis ventas y el KPI de arriba. */}
                                    <span className="w-[70px] flex-none text-right font-semibold tabular-nums">
                                        {priceLabel(saleAmountCents(s))}
                                    </span>
                                </HomeRow>
                            ))
                        )}
                    </HomeCard>

                    <HomeCard
                        title="Catálogo · top de mi tienda hoy"
                        meta={
                            <button
                                type="button"
                                className="text-primary hover:underline"
                                onClick={goSection('catalog')}
                            >
                                Ver catálogo completo →
                            </button>
                        }
                    >
                        {catalog.isPending ? (
                            <Loading />
                        ) : catalog.isError ? (
                            // Una lectura fallida NO se pinta como catálogo vacío: el vendedor
                            // concluiría que su tienda no tiene equipos.
                            <div role="alert" className="px-3 py-2.5 text-[11px]">
                                <p>No se pudo leer el catálogo de la tienda.</p>
                                <button
                                    type="button"
                                    className="mt-1 text-primary hover:underline"
                                    onClick={() => void catalog.refetch()}
                                >
                                    Reintentar
                                </button>
                            </div>
                        ) : topCatalog.length === 0 ? (
                            <HomeEmpty>
                                Esta tienda no tiene equipos cargados en el catálogo.
                            </HomeEmpty>
                        ) : (
                            topCatalog.map((p) => {
                                const sold = soldCount.get(p.sku) ?? 0;
                                return (
                                    <HomeRow key={p.sku} onClick={goWizard}>
                                        <span className="w-[104px] flex-none truncate font-mono text-[10.5px] text-muted-foreground">
                                            {p.sku}
                                        </span>
                                        <span className="min-w-[130px] flex-1 truncate font-medium">
                                            {itemDisplayName(p)}
                                        </span>
                                        <span className="flex-1 truncate text-[10.5px] text-muted-foreground">
                                            {variantCapacityLabel(p)}
                                        </span>
                                        <span className="flex-none">
                                            {sold > 0 ? (
                                                <Badge variant="success">{sold} hoy</Badge>
                                            ) : (
                                                <Badge variant="secondary">Popular</Badge>
                                            )}
                                        </span>
                                        <span className="w-[70px] flex-none text-right font-semibold tabular-nums">
                                            {priceLabel(p.cashPriceCents)}
                                        </span>
                                    </HomeRow>
                                );
                            })
                        )}
                    </HomeCard>
                </div>

                {/* Columna derecha — meta + prospectos + alertas + acciones rápidas */}
                <div className="flex min-h-0 flex-col gap-2.5">
                    <HomeCard title="Meta del día" bodyClassName="overflow-visible">
                        <div className="p-2.5">
                            <div className="flex items-baseline justify-between">
                                <div>
                                    <span className="text-[18px] font-semibold tabular-nums">
                                        {formatMXN(soldPesos)}
                                    </span>
                                    {hasGoal ? (
                                        <>
                                            <span className="text-[11px] text-muted-foreground">
                                                {' '}
                                                de{' '}
                                            </span>
                                            <span className="text-[12px] font-medium tabular-nums">
                                                {formatMXN(goalPesos)}
                                            </span>
                                        </>
                                    ) : null}
                                </div>
                                {hasGoal ? (
                                    <span className="text-[12px] font-semibold">{goalPct}%</span>
                                ) : null}
                            </div>
                            {hasGoal ? (
                                <div className="my-1.5 h-2.5 overflow-hidden rounded bg-muted">
                                    <div
                                        className="h-full rounded bg-success"
                                        style={{ width: `${String(Math.min(goalPct, 100))}%` }}
                                    />
                                </div>
                            ) : null}
                            <div className="mt-1.5 text-[10.5px] text-muted-foreground">
                                {!hasGoal
                                    ? 'Sin meta configurada para hoy. Pídesela a tu gerente.'
                                    : missing > 0
                                      ? `Faltan ${formatMXN(missing)} para la meta`
                                      : `¡Meta cumplida! Excedente: ${formatMXN(-missing)}`}
                            </div>
                        </div>
                    </HomeCard>

                    <HomeCard
                        title="Prospectos pendientes"
                        meta={
                            <button
                                type="button"
                                className="text-primary hover:underline"
                                onClick={goProspects}
                            >
                                Ver todos →
                            </button>
                        }
                        bodyClassName="max-h-[240px]"
                    >
                        {prospectsQuery.isPending ? (
                            <Loading />
                        ) : prospectsQuery.isError ? (
                            // Una lectura fallida NO se pinta como «sin prospectos»: el vendedor
                            // concluiría que no tiene a quién buscar hoy.
                            <div role="alert" className="px-3 py-2.5 text-[11px]">
                                <p>No se pudieron leer tus prospectos.</p>
                                <button
                                    type="button"
                                    className="mt-1 text-primary hover:underline"
                                    onClick={() => void prospectsQuery.refetch()}
                                >
                                    Reintentar
                                </button>
                            </div>
                        ) : overdue.length + today.length + upcoming.length === 0 ? (
                            <HomeEmpty>Sin prospectos pendientes.</HomeEmpty>
                        ) : (
                            <>
                                <ProspectGroup
                                    title="Vencidos"
                                    tone="destructive"
                                    items={overdue}
                                    label={() => 'venció'}
                                    onPick={resume}
                                />
                                <ProspectGroup
                                    title="Para hoy"
                                    tone="primary"
                                    items={today}
                                    label={() => 'hoy'}
                                    onPick={resume}
                                />
                                <ProspectGroup
                                    title="Próximos"
                                    tone="muted"
                                    items={upcoming}
                                    label={(p) =>
                                        new Date(p.nextContactAt).toLocaleDateString('es-MX', {
                                            weekday: 'short',
                                            day: '2-digit',
                                        })
                                    }
                                    onPick={resume}
                                />
                            </>
                        )}
                    </HomeCard>

                    <AlertsCard
                        title="Alertas"
                        emptyText="Sin alertas."
                        alerts={alerts.data ?? []}
                        isPending={alerts.isPending}
                        isError={alerts.isError}
                        onRetry={() => void alerts.refetch()}
                        renderAlert={(a) => (
                            <div
                                key={a.id}
                                className="border-b border-border/50 px-2.5 py-1.5 text-[11px] last:border-b-0"
                            >
                                <strong>{a.customerPhone}</strong> {a.text}
                            </div>
                        )}
                    />

                    <HomeCard title="Acciones rápidas" bodyClassName="overflow-visible">
                        <div className="grid grid-cols-2 gap-1.5 p-2">
                            <QuickAction
                                icon={<Plus className="size-4" aria-hidden />}
                                label="Nueva venta"
                                hint="Cliente nuevo"
                                onClick={goWizard}
                            />
                            <QuickAction
                                icon={<Search className="size-4" aria-hidden />}
                                label="Buscar cliente"
                                hint="Existente"
                                onClick={goSection('customers')}
                            />
                            <QuickAction
                                icon={<BookOpen className="size-4" aria-hidden />}
                                label="Catálogo"
                                hint="Ver equipos"
                                onClick={goSection('catalog')}
                            />
                            <QuickAction
                                icon={<Clock className="size-4" aria-hidden />}
                                label="Prospectos"
                                hint={
                                    hasProspectList
                                        ? `${String(overdue.length + today.length)} pendientes`
                                        : 'Ver pendientes'
                                }
                                onClick={goProspects}
                            />
                        </div>
                    </HomeCard>
                </div>
            </div>
        </div>
    );
}

const GROUP_TONE = {
    destructive: 'text-destructive bg-destructive/5',
    primary: 'text-primary bg-primary/5',
    muted: 'text-muted-foreground',
} as const;

function ProspectGroup({
    title,
    tone,
    items,
    label,
    onPick,
}: {
    title: string;
    tone: keyof typeof GROUP_TONE;
    items: Prospect[];
    label: (p: Prospect) => string;
    onPick: (p: Prospect) => () => void;
}) {
    if (items.length === 0) return null;
    return (
        <>
            <div
                className={`px-2 py-1 text-[9px] font-semibold uppercase tracking-wider ${GROUP_TONE[tone]}`}
            >
                {title} · {items.length}
            </div>
            {items.map((p) => (
                <button
                    key={p.prospectId}
                    type="button"
                    className="flex w-full cursor-pointer items-center gap-1.5 border-b border-border/50 px-2.5 py-[5px] text-left text-[11px] last:border-b-0 hover:bg-accent/40"
                    onClick={onPick(p)}
                >
                    {/* El folio del prospecto ES el dato que el vendedor canta por radio o WhatsApp;
                        el mock lo muestra igual. El `disable` va pegado a la línea del id: al
                        reformatearse el JSX quedó apuntando al `<span>` y dejó de cubrir nada. */}
                    <span className="w-[52px] flex-none truncate font-mono text-[10px] text-muted-foreground">
                        {/* eslint-disable-next-line no-restricted-syntax -- folio de negocio */}
                        {p.prospectId}
                    </span>
                    <span className="flex-1 truncate font-medium">{prospectName(p)}</span>
                    <span
                        className={`w-[65px] flex-none text-right text-[10px] ${tone === 'muted' ? '' : `font-semibold ${GROUP_TONE[tone].split(' ')[0]}`}`}
                    >
                        {label(p)}
                    </span>
                </button>
            ))}
        </>
    );
}

function QuickAction({
    icon,
    label,
    hint,
    onClick,
}: {
    icon: React.ReactNode;
    label: string;
    hint: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex flex-col items-center gap-1 rounded border border-border bg-card px-1.5 py-2.5 text-center transition-colors hover:border-primary/50 hover:bg-accent/50"
        >
            {icon}
            <span className="text-[11px] font-medium">{label}</span>
            <span className="text-[9.5px] text-muted-foreground">{hint}</span>
        </button>
    );
}
